import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { themeV2 } from '../styles/themeV2';
import LanguageSelector from './LanguageSelector';

const HeaderContainer = styled.header`
  position: sticky;
  top: 0;
  z-index: 40;
  backdrop-filter: blur(16px);
  background: rgba(255, 255, 255, 0.6);
  border-bottom: 1px solid ${themeV2.colors.slate[200]};
  
  @supports (backdrop-filter: blur(16px)) {
    background: rgba(255, 255, 255, 0.6);
  }
  
  @supports not (backdrop-filter: blur(16px)) {
    background: rgba(255, 255, 255, 0.7);
  }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 ${themeV2.spacing.lg};
  
  @media (min-width: ${themeV2.breakpoints.sm}) {
    padding: 0 ${themeV2.spacing['2xl']};
  }
  
  @media (min-width: ${themeV2.breakpoints.lg}) {
    padding: 0 ${themeV2.spacing['3xl']};
  }
`;

const Nav = styled.div`
  display: flex;
  height: 4rem;
  align-items: center;
  justify-content: space-between;
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${themeV2.spacing.sm};
  text-decoration: none;
  color: ${themeV2.colors.slate[900]};
`;

const LogoImage = styled.img`
  width: 7rem;
  height: auto;
`;

const LogoText = styled.span`
  font-weight: ${themeV2.typography.fontWeight.semibold};
  font-size: ${themeV2.typography.fontSize.lg};
`;

const NavLinks = styled.nav`
  display: none;
  align-items: center;
  gap: ${themeV2.spacing['2xl']};
  font-size: ${themeV2.typography.fontSize.sm};
  
  @media (min-width: ${themeV2.breakpoints.md}) {
    display: flex;
  }
  
  a {
    color: ${themeV2.colors.slate[700]};
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
      color: ${themeV2.colors.slate[900]};
    }
  }
`;

const MobileMenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${themeV2.borderRadius.xl};
  border: 1px solid ${themeV2.colors.slate[200]};
  padding: ${themeV2.spacing.sm} ${themeV2.spacing.lg};
  background: transparent;
  cursor: pointer;
  
  @media (min-width: ${themeV2.breakpoints.md}) {
    display: none;
  }
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const MobileMenu = styled.div`
  display: ${props => props.isOpen ? 'block' : 'none'};
  padding-bottom: ${themeV2.spacing.lg};
  
  @media (min-width: ${themeV2.breakpoints.md}) {
    display: none;
  }
`;

const MobileMenuGrid = styled.div`
  display: grid;
  gap: ${themeV2.spacing.sm};
  font-size: ${themeV2.typography.fontSize.sm};
  
  a {
    padding: ${themeV2.spacing.sm} 0;
    color: ${themeV2.colors.slate[700]};
    text-decoration: none;
    
    &:hover {
      color: ${themeV2.colors.slate[900]};
    }
  }
`;

const HeaderV2 = () => {
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <HeaderContainer>
      <Container>
        <Nav>
          <LogoContainer to="/">
            <LogoImage src="/assets/images/amalgamatech-logo.png" alt="AmalgamaTech" />
            <LogoText>AmalgamaTech</LogoText>
          </LogoContainer>
          
          <NavLinks>
            <Link to="/" onClick={() => window.scrollTo(0, 0)}>Inicio</Link>
            <Link to="/servicios" onClick={() => window.scrollTo(0, 0)}>{t('navigation.services')}</Link>
            <Link to="/nosotros" onClick={() => window.scrollTo(0, 0)}>{t('navigation.about')}</Link>
            <Link to="/contacto" onClick={() => window.scrollTo(0, 0)}>{t('navigation.contact')}</Link>
            <LanguageSelector />
          </NavLinks>
          
          <MobileMenuButton onClick={toggleMobileMenu}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </MobileMenuButton>
        </Nav>
        
        <MobileMenu isOpen={isMobileMenuOpen}>
          <MobileMenuGrid>
            <Link to="/" onClick={() => { closeMobileMenu(); window.scrollTo(0, 0); }}>Inicio</Link>
            <Link to="/servicios" onClick={() => { closeMobileMenu(); window.scrollTo(0, 0); }}>{t('navigation.services')}</Link>
            <Link to="/nosotros" onClick={() => { closeMobileMenu(); window.scrollTo(0, 0); }}>{t('navigation.about')}</Link>
            <Link to="/contacto" onClick={() => { closeMobileMenu(); window.scrollTo(0, 0); }}>{t('navigation.contact')}</Link>
            <div style={{ paddingTop: themeV2.spacing.sm }}>
              <LanguageSelector />
            </div>
          </MobileMenuGrid>
        </MobileMenu>
      </Container>
    </HeaderContainer>
  );
};

export default HeaderV2;